import { differenceInMinutes } from 'date-fns'

import { OnboardingFlowTypeEnum } from '@pages/Signup/Components/SignupStep'

import { CommonPaths } from '@utils/path'

import {
  AccountDetailedDto,
  KYCVerificationProvider,
  isAccountDormantUpdateNeeded,
} from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { TickmillProductType } from '@model/TickmillProductType'

import { determineProductState, hasProductDormantStatus } from './accountProductStatuses'

export const isAccountKycUpdateDetailsNotAllowed = (
  accountDetailed: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => {
  return !isAccountKycUpdateDetailsRequired(accountDetailed, product, true)
}

export const isAccountKycUpdateDetailsRequired = (
  accountDetailed: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined,
  ignoreDateDifferenceInMinutes = false
): boolean => {
  if (!accountDetailed) {
    return false
  }

  if (ignoreDateDifferenceInMinutes) {
    return allowKYCUpdate(accountDetailed)
  }

  const dateDifference = differenceInMinutes(
    new Date(),
    new Date(accountDetailed.detailsUpdateAvailableUntil ?? '')
  )

  const canUpdateDormant =
    isAccountDormantUpdateNeeded(accountDetailed) &&
    dateDifference >= 0 &&
    !hasProductDormantStatus(accountDetailed, product)

  return accountDetailed?.isKYCUpdateRequired || canUpdateDormant
}

export const allowKYCUpdate = (account: AccountDetailedDto | undefined): boolean => {
  if (!account) {
    return false
  }
  return account.isKYCUpdateNeeded || isAccountDormantUpdateNeeded(account)
}

export const isKYCUpdateRequired = (account: AccountDetailedDto | undefined): boolean => {
  if (!account) {
    return false
  }

  return account?.isKYCUpdateRequired
}

export const isDepositBeforeKYC = (account: AccountDetailedDto | undefined): boolean => {
  return (
    account?.visibilityConfiguration?.onboardingFlowType?.id ===
    OnboardingFlowTypeEnum.DepositBeforeKYC
  )
}

export const isSumsubNeeded = (account: AccountDetailedDto | undefined): boolean => {
  return isSumsubSupported(account) && !isAccountActivatedStatus(account)
}

export const isSumsubSupported = (account: AccountDetailedDto | undefined): boolean =>
  account?.visibilityConfiguration?.kycVerificationProvider === KYCVerificationProvider.Sumsub

export const getDocumentVerificationPath = (account: AccountDetailedDto | undefined): string => {
  if (isSumsubNeeded(account)) {
    return CommonPaths.Sumsub
  }
  return CommonPaths.DocumentManagement
}

export const isKycVerified = (
  account: AccountDetailedDto | undefined,
  product: TickmillProductType | undefined
): boolean => determineProductState(account, product)?.isKycVerified ?? false

export const isDepositPlaced = (account: AccountDetailedDto | undefined): boolean =>
  account?.visibilityConfiguration.hasPlacedDeposit ?? false

export const requireDepositBeforeKYC = (account: AccountDetailedDto | undefined): boolean => {
  return (
    !isAccountActivatedStatus(account) && isDepositBeforeKYC(account) && !isDepositPlaced(account)
  )
}
